import { RowSelectedEvent } from '@ag-grid-community/core';
import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ProgressModalComponent } from '@app/core/components/grid/modals/progress-modal/progress-modal.component';
import { FileItem, FileService } from '@app/core/services/file.service';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateService } from '@app/core/services/translate.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { DocPackTypeFormatStyleGet, DocPackTypeService } from '@app/services/doc-pack-type.service';
import { DocPackGet, DocPackPage, DocPackService, GetDocPackParams } from '@app/services/doc-pack.service';
import { MergeDocument, PdfService } from '@app/services/pdf.service';

@Component({
    selector: 'tt-doc-pack',
    templateUrl: './doc-pack.component.html',
    styleUrls: ['./doc-pack.component.css'],
})
export class DocPackComponent extends ComponentBaseComponent implements OnChanges, OnInit {
    @Input()
    public params?: GetDocPackParams;

    @Output()
    public deleted = new EventEmitter<void>();

    public get?: DocPackGet;

    public firstRender = false;

    public format?: DocPackTypeFormatStyleGet;

    public chaptersGrid?: GridOptions;

    public generatingPdf = false;

    public deleting = false;

    public saving = false;

    public resetting = false;

    public htmlProcessing = false;

    private translations = {
        confirm_title: '',
        confirm_message: '',
        confirm_ok_label: '',
        confirm_cancel_label: '',
        error: '',
        ok: '',
        retrieving_pages: '',
        retrieving_file_details_for: '',
        converting_page: '',
        to_pdf: '',
        merging_pdfs: '',
    };

    constructor(
        private docPack: DocPackService,
        private docPackType: DocPackTypeService,
        private modal: ModalService,
        private pdf: PdfService,
        private file: FileService,
        private translate: TranslateService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        super();
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;
            const { errorcode, errormessage } = await this.docPack.saveDocPack({ ...this.get, html_text_frontpage: this.base64UrlEncodeUnicode(this.get.html_text_frontpage), html_text_indexpage: this.base64UrlEncodeUnicode(this.get.html_text_indexpage) });

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    public async reset() {
        if (!this.get) return;

        try {
            this.resetting = true;

            const { errorcode, errormessage } = await this.docPack.resetDocPack(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.init();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.resetting = false;
        }
    }

    public async deleteValues() {
        if (!this.get) return;

        try {
            this.deleting = true;

            const confirmed = await this.modal.openConfirmDialog({ type: 'danger', title: this.translations.confirm_title, message: this.translations.confirm_message });

            if (!confirmed) return;

            const { errorcode, errormessage } = await this.docPack.deleteDocPack(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.deleted.emit();
                this.navigator?.back();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.deleting = false;
        }
    }

    public async generatePdf() {
        if (!this.get) return;

        this.generatingPdf = true;

        try {
            const progressDialog = this.modal.openProgressDialog({ indeterminate: true, max: 0, value: 0, label: this.translations.retrieving_pages, translateLabel: false }, { width: 'min(90rem, 90vw)' });
            const pages = (await this.docPack.getDocPackPages(this.get)).sort((a, b) => Number(a.filepages_keyno) - Number(b.filepages_keyno));

            progressDialog.updateIndeterminate(false);
            progressDialog.data.max = pages.length + 1;

            const responses = await Promise.allSettled(pages.map(async (document) => await this.getBase64Content(document, progressDialog)));
            const errors: unknown[] = [];

            for (let doc of responses) {
                if (doc.status === 'rejected') {
                    console.dir(doc);
                    errors.push(doc.reason);
                }
            }

            if (errors.length > 0) {
                const message = errors.reduce((acc, error) => {
                    let message = '';

                    if (!!error && typeof error === 'object') {
                        if ('data' in error && !!error.data && typeof error.data === 'object' && 'message' in error.data && typeof error.data.message === 'string') {
                            message = error.data.message;
                        } else if ('message' in error && typeof error.message === 'string') {
                            message = error.message;
                        }
                    }

                    return `${acc}${message} \n `;
                }, '');

                progressDialog.updateInvalidText(message as string);
                progressDialog.updateStatus('invalid');
                return;
            }

            progressDialog.label = this.translations.merging_pdfs;

            // if (!this.format) return;

            // @ts-ignore
            const base64documents = responses.map((response) => response.value);

            // @ts-ignore
            const mergedPdf = await this.pdf.mergePDFs(base64documents, {
                includeTableOfContents: true,
                template: this.format?.background_template ?? '',
                pageNumberConfig: {
                    showPageNumber: this.format?.show_pagenumber,
                    showPageNumberOnFirstPage: this.format?.show_pagenumber_firstpage,
                    x: +(this.format?.pagenumber_x ?? 0),
                    y: 0 + (this.format?.pagenumber_y ?? 0),
                    color: this.format?.pagenumber_color as any,
                    fontSize: +(this.format?.pagenumber_fontsize ?? 10),
                },
            });

            progressDialog.updateValue(progressDialog.data.value + 1);

            await this.downloadBase64(mergedPdf);

            progressDialog.updateStatus('finish');
        } catch (error) {
            console.error(error);
        } finally {
            this.generatingPdf = false;
        }
    }

    private async getBase64Content(document: DocPackPage, progressDialog: ProgressModalComponent): Promise<Omit<MergeDocument, 'pageRef' | 'index'>> {
        if (document.filecontent_type === 'HTML') {
            const pdf = await this.pdf.convertHTMLToPDF((this.format?.generated_style ?? '') + this.base64UrlDecode(document.filecontent_base64));

            progressDialog.label = `${this.translations.converting_page} ${document.pagetitle} ${this.translations.to_pdf}`;
            progressDialog.updateValue(progressDialog.data.value + 1);

            return {
                contentType: 'application/pdf',
                content: pdf,
                pagetitle: document.pagetitle,
                level: document.level,
                includeTemplate: true,
                rotationDegrees: Number(document.rotation_degrees ?? 0),
            };
        } else if (document.docarc_keyno !== '0' && !!document.filename) {
            let filedetails: FileItem | null = null;

            try {
                filedetails = await this.file.getFileDetails({ fileName: document.filename, suppressErrorMessage: true });
            } catch (error) {
                console.error(error);
                if (!!error && typeof error === 'object') {
                    if ('data' in error && !!error.data && typeof error.data === 'object' && 'message' in error.data && typeof error.data.message === 'string') {
                        throw Error(error.data.message + ` From chapter "${document.chaptertitle}"`);
                    } else {
                        throw error;
                    }
                }
            }

            if (!filedetails) throw Error('File does not have imagelink');

            const filelink = this.file.getTokenLink(filedetails!.imageLink!);
            const response = await fetch(filelink);
            const blob = await response.blob();
            const arraybuffer = await blob.arrayBuffer();

            progressDialog.label = `${this.translations.retrieving_file_details_for} ${document.pagetitle}`;
            progressDialog.updateValue(progressDialog.data.value + 1);

            if (filedetails.contentType?.includes('jpg') || filedetails.contentType?.includes('jpeg')) {
                return {
                    contentType: 'image/jpeg',
                    content: arraybuffer,
                    pagetitle: document.pagetitle,
                    level: document.level,
                    rotationDegrees: Number(document.rotation_degrees ?? 0),
                };
            } else if (filedetails.contentType?.includes('png')) {
                return {
                    contentType: 'image/png',
                    content: arraybuffer,
                    pagetitle: document.pagetitle,
                    level: document.level,
                    rotationDegrees: Number(document.rotation_degrees ?? 0),
                };
            } else if (filedetails.contentType?.includes('pdf')) {
                if (await this.pdf.isPdfEncrypted(arraybuffer)) {
                    throw Error(`File "${document.filename}" in chapter "${document.chaptertitle}" is encrypted.`);
                }

                return {
                    contentType: 'application/pdf',
                    content: arraybuffer,
                    pagetitle: document.pagetitle,
                    level: document.level,
                    rotationDegrees: Number(document.rotation_degrees ?? 0),
                };
            }
        }

        throw Error(`File "${document.filename}" in chapter "${document.chaptertitle}" could not be converted to pdf in app.`);
    }

    private async downloadBase64(base64: string) {
        const response = await fetch(base64);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');

        a.href = url;
        a.download = `${this.get?.docpack_name || 'dockpack'}.pdf`;
        a.click();
    }

    public onChapterRowSelected(event: RowSelectedEvent) {
        if (!this.get) return;

        if (event.node.isSelected() && !!event.node.id && '' + this.get.docpackchapter_keyno_selected !== event.node.id) {
            this.get.docpackchapter_keyno_selected = event.node.id;
        }
    }

    private readChaptersGrid() {
        if (!this.chaptersGrid) {
            this.chaptersGrid = new Grid()
                .setRememberId('w_docpack_docpackchapter_grid')
                .setLoadDataFunction({ method: 3290, parameters: () => this.get })
                .setKendoConfig({ selectable: 'single' });
        } else {
            this.chaptersGrid.gridfunc?.read();
        }
    }

    private async loadGet() {
        if (!!this.params) {
            const response = await this.docPack.getDocPack(this.params);
            this.get = { ...response, html_text_frontpage: this.base64UrlDecode(response.html_text_frontpage), html_text_indexpage: this.base64UrlDecode(response.html_text_indexpage) };
        }
    }

    private async loadFormat() {
        if (!!this.get) {
            this.format = await this.docPackType.getDocPackFormatStyle({ docpacktype_keyno: this.get.docpacktype_keyno });
        }
    }

    private async translateWords() {
        const response = await this.translate.translateBatch(this.translations);

        for (let k in response) {
            const key = k as keyof typeof this.translations;

            if (!!response[key]) this.translations[key] = response[key];
        }
    }

    private async init() {
        try {
            this.translateWords();
            await this.loadGet();
            this.navigator?.setHeading?.('p2_m_docpack', true);
            this.loadFormat();
            this.readChaptersGrid();
        } finally {
            this.firstRender = true;
        }
    }

    ngOnInit(): void {
        if (!!this.navigator) {
            this.params = this.navigator.params as any;
            this.init();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }
}
